// User Permission page v2 (WP-15).
// Role configuration and granular RBAC permission matrix matching WhatChimp §1.8.

import { useState, useEffect, useCallback } from 'react';
import { useI18n } from '../lib/i18n';
import { PageHeader } from '../components/ui/PageHeader';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { useToast } from '../components/ui/Toast';
import { useConfirm } from '../components/ui/ConfirmDialog';
import {
  PlusIcon,
  EditIcon,
  TrashIcon,
  CopyIcon,
  ShieldIcon,
} from '../components/icons';
import {
  roleList,
  roleDelete,
  type RoleDoc,
  ACTIONS,
  FEATURES,
} from '../lib/permissions';
import { RoleDrawer } from '../components/team/RoleDrawer';

export default function UserPermission() {
  const { t } = useI18n();
  const toast = useToast();
  const confirm = useConfirm();

  const [roles, setRoles] = useState<RoleDoc[]>([]);
  const [loading, setLoading] = useState(true);

  // Drawer state
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedRole, setSelectedRole] = useState<RoleDoc | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  const loadRoles = useCallback(async () => {
    try {
      const res = await roleList({});
      if (res.roles) {
        setRoles(res.roles);
      }
    } catch (err) {
      toast.error((err as Error).message);
    } finally {
      setLoading(false);
    }
    // `toast` deliberately excluded: an error toast must not re-run the loader.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    void loadRoles();
  }, [loadRoles]);

  function handleCreate() {
    setSelectedRole(null);
    setDrawerOpen(true);
  }

  function handleEdit(role: RoleDoc) {
    setSelectedRole(role);
    setDrawerOpen(true);
  }

  function handleDuplicate(role: RoleDoc) {
    // Duplicate opens drawer with role's permissions but no ID and a modified name
    setSelectedRole({
      ...role,
      id: '',
      name: `${role.name} (${t('roles.copy')})`,
      builtIn: false,
    });
    setDrawerOpen(true);
  }

  async function handleDelete(role: RoleDoc) {
    if (role.builtIn) return;
    if (!(await confirm({ title: t('common.areYouSure'), body: t('roles.deleteConfirm', { name: role.name }), danger: true }))) {
      return;
    }
    setBusyId(role.id);
    try {
      const res = await roleDelete({ id: role.id });
      if (res.ok) {
        toast.success(t('roles.deleted'));
        await loadRoles();
      }
    } catch (err) {
      toast.error((err as Error).message);
    } finally {
      setBusyId(null);
    }
  }

  function countPermissions(role: RoleDoc): number {
    let count = 0;
    for (const f of FEATURES) {
      for (const a of ACTIONS) {
        if (role.permissions?.[f]?.[a]) count++;
      }
    }
    return count;
  }

  return (
    <div className="space-y-6 pb-12">
      {/* Page Header */}
      <PageHeader
        title={t('roles.title')}
        subtitle={t('roles.subtitle')}
        action={
          <Button size="sm" onClick={handleCreate}>
            <PlusIcon size={14} className="me-1.5" />
            {t('roles.createRole')}
          </Button>
        }
      />

      {/* Role list / cards */}
      <div className="overflow-hidden rounded-card border border-line bg-white shadow-card dark:border-slate-800 dark:bg-slate-900">
        <div className="border-b border-line bg-surface/30 px-4 py-3 sm:px-5 dark:border-slate-800 dark:bg-slate-800/30">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-extrabold text-ink dark:text-slate-100">
              {t('roles.configuredRoles')} ({roles.length})
            </h2>
            <span className="text-xs text-ink-3 dark:text-slate-400">
              {t('roles.roleCountHint', { count: roles.length })}
            </span>
          </div>
        </div>

        {loading ? (
          <div className="grid h-48 place-items-center">
            <div className="h-8 w-8 animate-spin rounded-full border-4 border-brand border-t-transparent" />
          </div>
        ) : (
          <div className="divide-y divide-line dark:divide-slate-800">
            {roles.map((r) => {
              const permCount = countPermissions(r);
              const maxPerms = FEATURES.length * ACTIONS.length;
              const isBusy = r.id === busyId;

              return (
                <div
                  key={r.id}
                  className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 p-4 sm:px-5 hover:bg-surface/30 transition-colors dark:hover:bg-slate-800/20"
                >
                  <div className="flex items-start gap-3">
                    <span className="mt-0.5 grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-brand-50 text-brand dark:bg-brand-950/40 dark:text-brand-tint">
                      <ShieldIcon size={18} />
                    </span>
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="text-sm font-bold text-ink dark:text-slate-100">
                          {r.name}
                        </h3>
                        {r.builtIn ? (
                          <Badge variant="brand" size="sm">
                            {t('roles.builtIn')}
                          </Badge>
                        ) : (
                          <Badge variant="neutral" size="sm">
                            {t('roles.custom')}
                          </Badge>
                        )}
                      </div>
                      <p className="mt-1 text-xs text-ink-3 dark:text-slate-400">
                        {r.id === 'owner'
                          ? t('roles.ownerDescription')
                          : `${permCount} / ${maxPerms} ${t('roles.capabilitiesGranted')}`}
                      </p>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-2 self-end sm:self-center">
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={() => handleEdit(r)}
                      className="text-xs"
                    >
                      <EditIcon size={13} className="me-1" />
                      {r.builtIn ? t('roles.viewPermissions') : t('common.edit')}
                    </Button>

                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={() => handleDuplicate(r)}
                      className="text-xs"
                      title={t('roles.duplicate')}
                    >
                      <CopyIcon size={13} className="me-1" />
                      {t('roles.duplicate')}
                    </Button>

                    {!r.builtIn && (
                      <Button
                        size="sm"
                        variant="ghost"
                        disabled={isBusy}
                        onClick={() => void handleDelete(r)}
                        className="text-danger hover:bg-danger-50 hover:text-danger dark:hover:bg-rose-950/40"
                        title={t('roles.delete')}
                      >
                        <TrashIcon size={13} />
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Role Editor Drawer */}
      <RoleDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        role={selectedRole}
        onSaved={loadRoles}
      />
    </div>
  );
}
